import { importModuleByUrl, assertSameOriginOrMatch } from '@source-digital/sd-app-core'
import { type IBrokerContext } from '../../context'
import { type BrokerPluginFactory, type IBrokerPlugin } from '../plugin'
import { type IBrokerPluginInstaller, type BrokerPluginInstallOption, createPluginContext } from './base'

function isBrokerPluginFactory (obj: object): obj is BrokerPluginFactory {
  return typeof obj === 'function'
}
export interface ScriptBrokerPluginInstallOption extends BrokerPluginInstallOption<'script'> {
  src: string
  exportName?: string
  config?: any
}

export class ScriptBrokerPluginInstaller implements IBrokerPluginInstaller<'script', ScriptBrokerPluginInstallOption> {
  async install (ctx: IBrokerContext, installOption: ScriptBrokerPluginInstallOption): Promise<IBrokerPlugin> {
    const imported = await this.#importModule(installOption, ctx.initConfig.broker?.scriptPluginAllowedOrigins)
    const module = imported(installOption.config)
    const id = module.id
    if (id == null) throw new Error('The plugin does not have an id.')
    await module.install(createPluginContext(id, ctx, installOption))
    return module
  }

  async #importModule (installOption: ScriptBrokerPluginInstallOption, allowedOrigins?: string[]): Promise<BrokerPluginFactory> {
    assertSameOriginOrMatch(installOption.src, allowedOrigins)
    const exportNames: string[] = installOption.exportName != null ? [installOption.exportName] : ['default']
    const module = await importModuleByUrl(installOption.src, exportNames)

    if (module == null) {
      throw new Error(`Failed to import module: ${installOption.src}. It does not have any of the following exports: ${exportNames.join(', ')}`)
    }

    // TODO add validation for module
    if (!isBrokerPluginFactory(module)) {
      throw new Error(`Module is not a valid broker plugin: ${installOption.src}`)
    }
    return module
  }

  async uninstall (module: IBrokerPlugin, ctx: IBrokerContext): Promise<void> {
    await module?.uninstall?.(ctx)
  }
}
